import { useEffect, useState, useMemo } from "react";
import { BrowserRouter, Routes, Route, Link, useParams } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import axios from "axios";
import Umbrella from "./pages/Umbrella";
import { JobsList, JobDetail } from "./pages/Jobs";
import { LoansList, LoanDetail } from "./pages/Loans";
import CRMTab from "./pages/CRMTab";
import SyncTab from "./pages/SyncTab";
import {
  ArrowRight, CalendarDays, Check, Compass, Heart, MapPin, Menu, MessageCircle,
  Mountain, Phone, ShieldCheck, Sparkles, Star, Users, X, Clock, IndianRupee,
  Award, HandHeart, Route as RouteIcon, Mail, Instagram, Facebook, Youtube,
  Pencil, Trash2, FileText, Download, Globe, Search
} from "lucide-react";
import "@/App.css";
import "./pages/asg.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL || "https://arthsetuuglobal.in";
const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_pilgrimage-hub-test/artifacts/ko56o9zy_WhatsApp%20Image%202026-08-19%20at%207.07.24%20PM.jpeg";
const PHONE = "8789299576";
const WHATSAPP = "918789299576";
const EMAIL_PRIMARY = "arthsetuuglobal@gmail.com";
const EMAIL_SECONDARY = "yatra@arthsetuuglobal.in";
const OFFICE_ADDRESS = "Arth Setuu Global, 2nd Floor, Office No. 2, Bandhu Market, Opposite Aditya Vision, Tribhuvan More, Khagaul Road, Danapur, Patna 801105";
const OFFICE_ADDRESS_LINES = [
  "2nd Floor, Office No. 2, Bandhu Market",
  "Opposite Aditya Vision, Tribhuvan More",
  "Khagaul Road, Danapur, Patna 801105"
];

function SEO({ title, description, image, canonical, schema, keywords }) {
  const t = title || "Arth Setuu Global — Bridging Faith, Building Journeys";
  const d = description || "Curated spiritual, pilgrimage and heritage journeys across India, Nepal and beyond. Book yatras with comfort, care and quiet devotion.";
  const img = image || LOGO_URL;
  const url = canonical || (typeof window !== 'undefined' ? window.location.href : "https://arthsetuuglobal.in/");
  return (
    <Helmet>
      <title>{t}</title>
      <meta name="description" content={d} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Arth Setuu Global" />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:image" content={img} />
      <meta property="og:url" content={url} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image" content={img} />
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
}

function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "name": "Arth Setuu Global",
    "url": "https://arthsetuuglobal.in",
    "logo": LOGO_URL,
    "description": "Curated spiritual, pilgrimage and heritage journeys across India, Nepal and beyond.",
    "telephone": "+91 8789299576",
    "email": EMAIL_PRIMARY,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2nd Floor, Office No. 2, Bandhu Market, Opposite Aditya Vision, Tribhuvan More, Khagaul Road",
      "addressLocality": "Danapur",
      "addressRegion": "Bihar",
      "postalCode": "801105",
      "addressCountry": "IN"
    },
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91 8789299576",
      "contactType": "customer service",
      "areaServed": "IN",
      "availableLanguage": ["en", "hi"]
    }
  };
}

const FALLBACK = [
  { id: "1", title: "7 Jyotirlinga Yatra", slug: "7-jyotirlinga-yatra", category: "Shiva", destination: "Western & Central India",
    duration: "11 days / 10 nights", price: "₹18,500", featured: true,
    short_description: "A thoughtfully paced route through seven revered Jyotirlinga shrines of Lord Shiva.",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Mahakaleshwar_Temple%2C_Ujjain.jpg/1920px-Mahakaleshwar_Temple%2C_Ujjain.jpg",
    departures: [{ date: "29 Sep 2026", label: "Reservation Coach" }],
    destinations: ["Mahakaleshwar", "Somnath", "Trimbakeshwar", "Bhimashankar"] },
  { id: "2", title: "Char Dham Yatra", slug: "char-dham-yatra", category: "Himalayan", destination: "Uttarakhand",
    duration: "15 days / 14 nights", price: "₹29,900", featured: true,
    short_description: "Journey through the sacred Himalayan abodes of Yamunotri, Gangotri, Kedarnath and Badrinath.",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Kedarnath_Temple_in_Rainy_season.jpg/1920px-Kedarnath_Temple_in_Rainy_season.jpg",
    departures: [{ date: "15 May 2027", label: "Yatra departure" }],
    destinations: ["Yamunotri", "Gangotri", "Kedarnath", "Badrinath"] },
  { id: "3", title: "Ganga Sagar Yatra", slug: "ganga-sagar-yatra", category: "Dham Yatras", destination: "West Bengal",
    duration: "8 days / 7 nights", price: "₹11,600", featured: false,
    short_description: "A river-led pilgrimage to the sacred confluence where the Ganga meets the sea.",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b2/Gangotri_%28ganga_river%29.jpg/1920px-Gangotri_%28ganga_river%29.jpg",
    departures: [{ date: "18 Jan 2027", label: "Makar Sankranti" }],
    destinations: ["Kolkata", "Sagar Island", "Kapil Muni Ashram"] },
  { id: "4", title: "Rameswaram Dham Yatra", slug: "rameswaram-dham-yatra", category: "Vishnu", destination: "Tamil Nadu",
    duration: "9 days / 8 nights", price: "₹16,800", featured: true,
    short_description: "Sacred pilgrimage to Rameswaram, one of the four Char Dhams of India.",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/84/Ramanathaswamy_temple7.JPG/1920px-Ramanathaswamy_temple7.JPG",
    departures: [{ date: "12 Oct 2026", label: "Autumn departure" }],
    destinations: ["Rameswaram", "Madurai", "Kanyakumari"] },
  { id: "5", title: "Nepal Videsh Yatra", slug: "nepal-videsh-yatra", category: "Shiva", destination: "Nepal",
    duration: "10 days / 9 nights", price: "₹24,500", featured: false,
    short_description: "Sacred temples of Pashupatinath and the spiritual essence of the Himalayan kingdom.",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1f/Pashupatinath_Temple-2020.jpg/1920px-Pashupatinath_Temple-2020.jpg",
    departures: [{ date: "05 Nov 2026", label: "Post-monsoon" }],
    destinations: ["Kathmandu", "Pashupatinath", "Muktinath", "Pokhara"] },
  { id: "6", title: "Rishikesh-Haridwar Yatra", slug: "rishikesh-haridwar-yatra", category: "Himalayan", destination: "Uttarakhand",
    duration: "6 days / 5 nights", price: "₹9,800", featured: false,
    short_description: "Sacred ghats, Ganga aarti and the spiritual gateway to the Himalayas.",
    image: "https://upload.wikimedia.org/wikipedia/commons/0/0f/Evening_view_of_Har-ki-Pauri%2C_Haridwar.jpg",
    departures: [{ date: "22 Oct 2026", label: "Yatra departure" }],
    destinations: ["Rishikesh", "Haridwar", "Neelkanth Mahadev"] },
];

const DEITY_CATEGORIES = [
  { name: "Shiva", tagline: "Jyotirlingas & Kailash", image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Mahakaleshwar_Temple%2C_Ujjain.jpg/1920px-Mahakaleshwar_Temple%2C_Ujjain.jpg", slug: "shiva" },
  { name: "Shakti", tagline: "Devi shrines & Peethas", image: "https://upload.wikimedia.org/wikipedia/commons/0/0f/Evening_view_of_Har-ki-Pauri%2C_Haridwar.jpg", slug: "shakti" },
  { name: "Vishnu", tagline: "Dham & Vaishnavite routes", image: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Badrinath_Temple_%2C_Uttarakhand.jpg/1920px-Badrinath_Temple_%2C_Uttarakhand.jpg", slug: "vishnu" },
  { name: "Himalayan", tagline: "Char Dham & sacred peaks", image: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Kedarnath_Temple_in_Rainy_season.jpg/1920px-Kedarnath_Temple_in_Rainy_season.jpg", slug: "himalayan" },
];

const STORIES = [
  { title: "Walk the Sacred Ghats of Kashi", tag: "Varanasi", image: "https://upload.wikimedia.org/wikipedia/commons/0/0f/Evening_view_of_Har-ki-Pauri%2C_Haridwar.jpg",
    desc: "Where the Ganga bends and lamps are lit at dawn — a city that has held prayers for millennia." },
  { title: "Seek the Divine in the Himalayas", tag: "Uttarakhand", image: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Kedarnath_Temple_in_Rainy_season.jpg/1920px-Kedarnath_Temple_in_Rainy_season.jpg",
    desc: "Yamunotri to Badrinath — four abodes woven into snow, silence, and ancient devotion." },
  { title: "Follow the Jyotirlinga Trail", tag: "Sacred Circuit", image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Mahakaleshwar_Temple%2C_Ujjain.jpg/1920px-Mahakaleshwar_Temple%2C_Ujjain.jpg",
    desc: "Twelve luminous shrines of Shiva across the land — a journey that traces India's spiritual spine." },
];

const TESTIMONIALS = [
  { name: "Priya Sharma", city: "Mumbai", journey: "Char Dham Yatra 2026", text: "Every detail — from the reservation coach to the aarti timing — felt cared for. Arth Setuu turned a difficult route into a truly moving journey." },
  { name: "Ramesh Iyer", city: "Chennai", journey: "7 Jyotirlinga Yatra", text: "For 68-year-old parents, comfort and clarity mattered most. The team stayed in touch through every stop. We felt looked after." },
  { name: "Ananya Das", city: "Kolkata", journey: "Nepal Videsh Yatra", text: "Pashupatinath at dawn will stay with me. The route was paced with real understanding of what pilgrimage means." },
];

function usePackages() {
  const [data, setData] = useState(FALLBACK);
  useEffect(() => {
    axios.get(`${API}/packages`).then(r => r.data.length && setData(r.data)).catch(() => setData(FALLBACK));
  }, []);
  return data;
}

function parseDepartureDate(str) {
  if (!str || /details|tbd|confirm/i.test(str)) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function useCountdown(target) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return { expired: true, days: 0, hours: 0, mins: 0, secs: 0 };
  return {
    expired: false,
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
    tick,
  };
}

function urgencyClass(days) {
  if (days <= 7) return "urgent";
  if (days <= 21) return "warm";
  return "";
}

function CountdownPill({ dateStr, testid }) {
  const target = useMemo(() => parseDepartureDate(dateStr), [dateStr]);
  const c = useCountdown(target);
  if (!target || !c) return null;
  if (c.expired) return <span className="countdown-pill expired" data-testid={testid}><Clock size={12} /> Departed</span>;
  const label = c.days > 0
    ? `Departs in ${c.days}d ${c.hours}h`
    : `Departs in ${c.hours}h ${c.mins}m`;
  return (
    <span className={`countdown-pill ${urgencyClass(c.days)}`} data-testid={testid}>
      <Clock size={12} /> {label}
    </span>
  );
}

function CountdownBlock({ dateStr, testid }) {
  const target = useMemo(() => parseDepartureDate(dateStr), [dateStr]);
  const c = useCountdown(target);
  if (!target || !c) return null;
  if (c.expired) {
    return (
      <div className="countdown-block expired" data-testid={testid}>
        <span className="cb-label">This departure has passed</span>
      </div>
    );
  }
  const urgent = urgencyClass(c.days);
  return (
    <div className={`countdown-block ${urgent}`} data-testid={testid}>
      <span className="cb-label">
        {urgent === "urgent" ? "⚡ Last chance — departs in" : urgent === "warm" ? "🔥 Book soon — departs in" : "Next departure in"}
      </span>
      <div className="cb-boxes">
        <div><b>{String(c.days).padStart(2, "0")}</b><small>Days</small></div>
        <div><b>{String(c.hours).padStart(2, "0")}</b><small>Hours</small></div>
        <div><b>{String(c.mins).padStart(2, "0")}</b><small>Min</small></div>
        <div><b>{String(c.secs).padStart(2, "0")}</b><small>Sec</small></div>
      </div>
    </div>
  );
}

function BrandLockup({ dark = false }) {
  return (
    <Link to="/" className="brand-lockup" data-testid="site-brand">
      <img src={LOGO_URL} alt="Arth Setuu Global" />
      <div className="brand-lockup-text">
        <b style={dark ? { color: '#fff' } : {}}>Arth Setuu Global</b>
        <small>Bridging Faith · Journeys</small>
      </div>
    </Link>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header" data-testid="site-header">
      <BrandLockup />
      <nav className={`site-nav ${open ? "open" : ""}`} data-testid="site-nav">
        <Link to="/packages" data-testid="nav-yatras" onClick={() => setOpen(false)}>Yatras</Link>
        <Link to="/packages" data-testid="nav-departures" onClick={() => setOpen(false)}>Departures</Link>
        <a href="/yatra#stories" data-testid="nav-stories" onClick={() => setOpen(false)}>Stories</a>
        <a href="/yatra#why" data-testid="nav-why" onClick={() => setOpen(false)}>Why Us</a>
        <a href="/yatra#enquiry" data-testid="nav-contact" onClick={() => setOpen(false)}>Contact</a>
      </nav>
      <div className="header-actions">
        <a className="header-phone" href={`tel:${PHONE}`} data-testid="header-phone">
          <Phone size={16} /> +91 {PHONE}
        </a>
        <a
          className="btn btn-whatsapp btn-sm"
          href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20would%20like%20to%20plan%20a%20yatra%20with%20Arth%20Setuu%20Global.`}
          target="_blank" rel="noreferrer" data-testid="header-whatsapp"
        >
          <MessageCircle size={15} /> WhatsApp
        </a>
        <button className="menu-btn" onClick={() => setOpen(!open)} data-testid="menu-toggle" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>
    </header>
  );
}

function Hero() {
  const featured = FALLBACK[0]; // 7 Jyotirlinga
  return (
    <section className="hero" data-testid="hero-section">
      <video
        className="hero-video"
        autoPlay muted loop playsInline
        poster={featured.image}
        data-testid="hero-video"
      >
        <source src="https://videos.pexels.com/video-files/857192/857192-hd_1920_1080_30fps.mp4" type="video/mp4" />
        <source src="https://videos.pexels.com/video-files/2098988/2098988-hd_1920_1080_30fps.mp4" type="video/mp4" />
      </video>
      <div className="hero-bg" style={{ backgroundImage: `url(${featured.image})` }} />
      <div className="hero-content">
        <span className="eyebrow" data-testid="hero-eyebrow"><Sparkles size={14} /> Bridging Faith · Building Journeys</span>
        <h1>Journeys that bring you <em>closer to the divine.</em></h1>
        <p className="hero-sub">
          Curated spiritual, pilgrimage and heritage journeys across India, Nepal and beyond —
          designed with comfort, care and quiet devotion.
        </p>
        <div className="hero-ctas">
          <Link to="/packages" className="btn btn-primary" data-testid="hero-explore-btn">
            Explore Yatras <ArrowRight size={16} />
          </Link>
          <a
            className="btn btn-whatsapp"
            href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20would%20like%20to%20plan%20a%20yatra.`}
            target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn"
          >
            <MessageCircle size={16} /> Book on WhatsApp
          </a>
        </div>
        <div className="hero-stats" data-testid="hero-stats">
          <div className="hero-stat"><b>25+</b><span>Curated Yatras</span></div>
          <div className="hero-stat"><b>3,500+</b><span>Happy Pilgrims</span></div>
          <div className="hero-stat"><b>India · Nepal</b><span>& Beyond</span></div>
        </div>
      </div>
      <div className="hero-featured" data-testid="hero-featured-card">
        <span className="eyebrow"><Star size={12} fill="currentColor" /> Featured Yatra</span>
        <h4>7 Jyotirlinga Yatra</h4>
        <div className="hero-featured-meta">
          <span>{featured.duration}</span>
          <strong>{featured.price}</strong>
        </div>
        <Link to={`/yatra/${featured.slug}`} className="link-arrow" data-testid="hero-featured-link">
          View journey <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

function IntroBand() {
  return (
    <section className="intro-band" data-testid="intro-band">
      <div>
        <div className="om-mark">ॐ</div>
        <span className="eyebrow maroon">The Arth Setuu Approach</span>
        <h2 style={{ marginTop: '0.8rem' }}>Travel with reverence.<br /><em className="italic-accent">Arrive with ease.</em></h2>
      </div>
      <p>
        A pilgrimage is more than a route between destinations. It is time to pause,
        to notice, and to move through India's sacred landscapes with thoughtful care.
        We shape every yatra so the practical stays clear — and you can stay present.
      </p>
    </section>
  );
}

function DeitySection() {
  return (
    <section className="deity-grid" id="journeys" data-testid="deity-section">
      <div className="section-heading">
        <span className="eyebrow"><Sparkles size={13} /> Explore by Spiritual Path</span>
        <h2>Sacred Journeys, <em className="italic-accent">Considered.</em></h2>
        <p>Follow the paths of Shiva, Shakti, Vishnu and the Himalayan devas — curated routes that honour each tradition.</p>
      </div>
      <div className="deity-cards">
        {DEITY_CATEGORIES.map(cat => (
          <Link to={`/packages?category=${cat.slug}`} key={cat.slug} className="deity-card"
            style={{ '--bg': `url(${cat.image})` }} data-testid={`deity-card-${cat.slug}`}>
            <div className="deity-card-content">
              <span className="eyebrow">{cat.tagline}</span>
              <h3>{cat.name}</h3>
              <small>Discover routes <ArrowRight size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /></small>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function YatraCard({ pkg, large = false }) {
  const dest = Array.isArray(pkg.destinations) && pkg.destinations.length
    ? pkg.destinations.slice(0, 3).join(" · ") : pkg.destination;
  const nextDep = pkg.departures?.[0]?.date || "Details to be confirmed";
  return (
    <article className={`yatra-card ${large ? "large" : ""}`} data-testid={`yatra-card-${pkg.slug}`}>
      <div className="yatra-card-image" style={{ backgroundImage: `url(${pkg.image})` }}>
        {pkg.featured && <div className="yatra-badge">Featured</div>}
        <div className="yatra-price-tag">
          <small>Starting from</small>
          <strong>{pkg.price}</strong>
        </div>
      </div>
      <div className="yatra-card-body">
        <span className="eyebrow">{pkg.category} · {pkg.destination}</span>
        <h3>{pkg.title}</h3>
        <p className="desc">{pkg.short_description}</p>
        <div className="yatra-facts">
          <div className="yatra-fact"><Clock size={15} /><span><b>{pkg.duration}</b></span></div>
          <div className="yatra-fact"><CalendarDays size={15} /><span>{nextDep}</span></div>
          <div className="yatra-fact" style={{ gridColumn: '1 / -1' }}>
            <MapPin size={15} /><span>{dest}</span>
          </div>
        </div>
        <CountdownPill dateStr={nextDep} testid={`countdown-${pkg.slug}`} />
        <div className="yatra-cta-row">
          <Link to={`/yatra/${pkg.slug}`} className="btn btn-outline btn-sm" data-testid={`yatra-view-${pkg.slug}`}>
            View Journey
          </Link>
          <a
            className="btn btn-whatsapp btn-sm"
            href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20am%20interested%20in%20${encodeURIComponent(pkg.title)}`}
            target="_blank" rel="noreferrer" data-testid={`yatra-book-${pkg.slug}`}
          >
            <MessageCircle size={14} /> Book Now
          </a>
        </div>
      </div>
    </article>
  );
}

function FeaturedYatras({ packages }) {
  const featured = packages.filter(p => p.featured).slice(0, 3);
  const shown = featured.length ? featured : packages.slice(0, 3);
  return (
    <section className="featured-yatras" data-testid="featured-yatras">
      <div className="section-heading" style={{ textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '3rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <span className="eyebrow"><Star size={13} fill="currentColor" /> Featured Yatras</span>
          <h2 style={{ marginTop: '0.8rem' }}>Curated Journeys, <em className="italic-accent">This Season.</em></h2>
        </div>
        <Link to="/packages" className="link-arrow" data-testid="view-all-yatras">View all yatras <ArrowRight size={14} /></Link>
      </div>
      <div className="yatra-grid">
        {shown.map((p, i) => <YatraCard key={p.slug} pkg={p} large={i === 0} />)}
      </div>
    </section>
  );
}

function Departures({ packages }) {
  const rows = packages.slice(0, 5);
  return (
    <section className="departures" id="departures" data-testid="departures-section">
      <div className="departures-header">
        <div>
          <span className="eyebrow cream"><CalendarDays size={13} /> Dates to Hold</span>
          <h2 style={{ marginTop: '0.8rem', color: '#fff' }}>Upcoming <em className="italic-accent" style={{ color: 'var(--marigold)' }}>Departures</em></h2>
        </div>
        <Link to="/packages" className="link-arrow" data-testid="view-all-departures">View all departures <ArrowRight size={14} /></Link>
      </div>
      <div className="departure-list">
        {rows.map(p => (
          <Link to={`/yatra/${p.slug}`} key={p.slug} className="departure-row" data-testid={`departure-${p.slug}`}>
            <div className="departure-icon"><CalendarDays size={20} /></div>
            <div>
              <b>{p.title}</b>
              <small>{p.category} · {p.destination}</small>
            </div>
            <div><b style={{ color: 'var(--marigold)' }}>{p.departures?.[0]?.date || "TBD"}</b><small>{p.departures?.[0]?.label || "Departure"}</small></div>
            <CountdownPill dateStr={p.departures?.[0]?.date} testid={`dep-countdown-${p.slug}`} />
            <span className="price">{p.price}</span>
            <ArrowRight className="arrow" size={20} />
          </Link>
        ))}
      </div>
    </section>
  );
}

function Stories() {
  return (
    <section className="stories" id="stories" data-testid="stories-section">
      <div className="section-heading">
        <span className="eyebrow"><Mountain size={13} /> Destination Stories</span>
        <h2>Where every step <em className="italic-accent">has meaning.</em></h2>
      </div>
      <div className="story-grid">
        {STORIES.map((s, i) => (
          <div key={i} className="story-card" data-testid={`story-card-${i}`}>
            <div className="story-card-bg" style={{ '--bg': `url(${s.image})` }} />
            <div className="story-card-content">
              <span className="eyebrow">{s.tag}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhyUs() {
  const items = [
    { icon: <RouteIcon size={22} />, title: "Curated Routes", text: "Every yatra is thoughtfully paced — respecting devotion, comfort and the pilgrim's own rhythm." },
    { icon: <HandHeart size={22} />, title: "Dedicated Care", text: "One trusted advisor from first enquiry to the final darshan. No handoffs, no confusion." },
    { icon: <ShieldCheck size={22} />, title: "Transparent Pricing", text: "Clear inclusions, clear exclusions. What you see is what your journey covers." },
    { icon: <Users size={22} />, title: "Comfortable Group Travel", text: "Reservation coaches, well-selected accommodation, and paced itineraries built for real people." },
    { icon: <Award size={22} />, title: "Experienced Team", text: "Years of on-ground pilgrimage operations across India, Nepal and beyond." },
    { icon: <Heart size={22} />, title: "Personal Guidance", text: "For senior pilgrims, families, medical needs — thoughtful support at every step." },
  ];
  return (
    <section className="why-us" id="why" data-testid="why-us-section">
      <div className="section-heading">
        <span className="eyebrow"><Sparkles size={13} /> Why Arth Setuu Global</span>
        <h2>There is a <em className="italic-accent">quieter way</em> to journey.</h2>
        <p>From the first question to the final darshan — clarity, care and quiet capability at every step.</p>
      </div>
      <div className="why-grid">
        {items.map((w, i) => (
          <div key={i} className="why-item" data-testid={`why-item-${i}`}>
            <div className="why-item-icon">{w.icon}</div>
            <h3>{w.title}</h3>
            <p>{w.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="testimonials" data-testid="testimonials-section">
      <div className="section-heading">
        <span className="eyebrow"><Heart size={13} /> Words from Pilgrims</span>
        <h2>Journeys, <em className="italic-accent">remembered.</em></h2>
      </div>
      <div className="testimonial-grid">
        {TESTIMONIALS.map((t, i) => (
          <div key={i} className="testimonial-card" data-testid={`testimonial-${i}`}>
            <div className="testimonial-quote">"</div>
            <p>{t.text}</p>
            <div className="testimonial-author">
              <div className="testimonial-author-avatar">{t.name[0]}</div>
              <div>
                <b>{t.name}</b>
                <small>{t.city} · {t.journey}</small>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function EnquirySection({ selected = "" }) {
  return (
    <section className="enquiry-section" id="enquiry" data-testid="enquiry-section">
      <div className="enquiry-left">
        <span className="eyebrow"><MessageCircle size={13} /> Begin with a Conversation</span>
        <h2>Plan your <em className="italic-accent">sacred journey.</em></h2>
        <p>Share a few details and one of our journey advisors will reach out with route options, dates and honest guidance.</p>
        <div className="enquiry-features">
          <div className="enquiry-feature"><Check size={16} /> No obligation, no pushy calls</div>
          <div className="enquiry-feature"><Check size={16} /> Personal reply within 24 hours</div>
          <div className="enquiry-feature"><Check size={16} /> Custom itineraries on request</div>
        </div>
        <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <a href={`tel:${PHONE}`} className="link-arrow" data-testid="enquiry-phone-link">
            <Phone size={14} /> Call +91 {PHONE}
          </a>
          <a
            href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20would%20like%20to%20plan%20a%20yatra.`}
            target="_blank" rel="noreferrer" className="link-arrow"
            style={{ color: 'var(--whatsapp)', borderColor: 'var(--whatsapp)' }}
            data-testid="enquiry-whatsapp-link"
          >
            <MessageCircle size={14} /> WhatsApp us instantly
          </a>
          <div className="enquiry-address" data-testid="enquiry-address">
            <MapPin size={16} />
            <div>
              <b>Visit our office</b>
              <span>{OFFICE_ADDRESS_LINES.join(", ")}</span>
            </div>
          </div>
        </div>
      </div>
      <EnquiryForm selected={selected} />
    </section>
  );
}

function EnquiryForm({ selected = "" }) {
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "",
    package: selected, adults: 1, children: 0, message: "", consent: false,
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { setError("Please confirm consent to be contacted."); return; }
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "Website" });
      setSent(true);
    } catch (err) {
      setError("We couldn't save your enquiry. Please try WhatsApp or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="form-success" data-testid="enquiry-success">
        <Check size={48} />
        <h3>Your enquiry is with us.</h3>
        <p>Namaste 🙏 We'll reach out shortly with journey details.</p>
        <a
          className="btn btn-whatsapp"
          href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20just%20submitted%20an%20enquiry.`}
          target="_blank" rel="noreferrer" data-testid="success-whatsapp"
        >
          <MessageCircle size={16} /> Continue on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form className="enquiry-form" onSubmit={submit} data-testid="enquiry-form">
      <h3>Send an enquiry</h3>
      <div className="form-grid">
        <label className="form-label">Name
          <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Your full name" data-testid="enquiry-name" />
        </label>
        <label className="form-label">Mobile
          <input required value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })}
            placeholder="10-digit mobile" data-testid="enquiry-mobile" />
        </label>
      </div>
      <div className="form-grid">
        <label className="form-label">Email (optional)
          <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="you@example.com" data-testid="enquiry-email" />
        </label>
        <label className="form-label">City
          <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
            placeholder="Your city" data-testid="enquiry-city" />
        </label>
      </div>
      <label className="form-label">Journey of interest
        <input value={form.package} onChange={e => setForm({ ...form, package: e.target.value })}
          placeholder="e.g. Char Dham Yatra" data-testid="enquiry-package" />
      </label>
      <div className="form-grid">
        <label className="form-label">Adults
          <input type="number" min="1" value={form.adults} onChange={e => setForm({ ...form, adults: +e.target.value })}
            data-testid="enquiry-adults" />
        </label>
        <label className="form-label">Children
          <input type="number" min="0" value={form.children} onChange={e => setForm({ ...form, children: +e.target.value })}
            data-testid="enquiry-children" />
        </label>
      </div>
      <label className="form-label">Message (optional)
        <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
          placeholder="Travel window, group size, questions..." data-testid="enquiry-message" />
      </label>
      <label className="consent-row">
        <input type="checkbox" checked={form.consent} onChange={e => setForm({ ...form, consent: e.target.checked })}
          data-testid="enquiry-consent" />
        <span>I agree to Arth Setuu Global processing my information to respond to my travel enquiry and provide relevant travel services.</span>
      </label>
      {error && <div className="form-error" data-testid="enquiry-error">{error}</div>}
      <button type="submit" className="btn btn-primary" disabled={loading} data-testid="enquiry-submit">
        {loading ? "Sending..." : <>Send enquiry <ArrowRight size={16} /></>}
      </button>
    </form>
  );
}

function FinalCTA() {
  return (
    <section className="final-cta" data-testid="final-cta">
      <span className="eyebrow"><Sparkles size={13} /> Your Journey Awaits</span>
      <h2>Your sacred journey <em className="italic-accent" style={{ color: 'var(--marigold)' }}>begins here.</em></h2>
      <p>Speak to a journey advisor today. Plan your yatra with clarity, comfort and quiet devotion.</p>
      <div className="final-cta-actions">
        <a
          className="btn btn-whatsapp"
          href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20would%20like%20to%20plan%20a%20yatra.`}
          target="_blank" rel="noreferrer" data-testid="final-whatsapp"
        >
          <MessageCircle size={17} /> Book on WhatsApp
        </a>
        <a className="btn btn-outline-light" href={`tel:${PHONE}`} data-testid="final-call">
          <Phone size={17} /> Call +91 {PHONE}
        </a>
      </div>
    </section>
  );
}

function StickyCTAs() {
  return (
    <>
      <div className="sticky-ctas" data-testid="sticky-ctas">
        <a
          className="sticky-cta whatsapp"
          href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20would%20like%20to%20plan%20a%20yatra.`}
          target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid="sticky-whatsapp"
        >
          <MessageCircle size={26} />
        </a>
        <a
          className="sticky-cta call"
          href={`tel:${PHONE}`} aria-label="Call" data-testid="sticky-call"
        >
          <Phone size={24} />
        </a>
      </div>
      <a
        className="sticky-whatsapp-bar"
        href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20would%20like%20to%20plan%20a%20yatra.`}
        target="_blank" rel="noreferrer" data-testid="sticky-whatsapp-bar"
      >
        <MessageCircle size={18} /> Book on WhatsApp — +91 {PHONE}
      </a>
    </>
  );
}

function Footer() {
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="footer-top">
        <div className="footer-brand">
          <BrandLockup dark />
          <p>Bridging Faith, Building Journeys. Thoughtful spiritual, pilgrimage and heritage travel across India, Nepal and beyond.</p>
          <div style={{ display: 'flex', gap: '0.8rem', marginTop: '1.5rem' }}>
            <a href="#" aria-label="Instagram" data-testid="footer-instagram"><Instagram size={20} /></a>
            <a href="#" aria-label="Facebook" data-testid="footer-facebook"><Facebook size={20} /></a>
            <a href="#" aria-label="Youtube" data-testid="footer-youtube"><Youtube size={20} /></a>
          </div>
        </div>
        <div className="footer-col">
          <h5>Yatras</h5>
          <ul>
            <li><Link to="/yatra/7-jyotirlinga-yatra" data-testid="footer-link-jyotirlinga">7 Jyotirlinga Yatra</Link></li>
            <li><Link to="/yatra/char-dham-yatra" data-testid="footer-link-chardham">Char Dham Yatra</Link></li>
            <li><Link to="/yatra/ganga-sagar-yatra" data-testid="footer-link-gangasagar">Ganga Sagar Yatra</Link></li>
            <li><Link to="/yatra/rameswaram-dham-yatra" data-testid="footer-link-rameswaram">Rameswaram Yatra</Link></li>
            <li><Link to="/yatra/nepal-videsh-yatra" data-testid="footer-link-nepal">Nepal Yatra</Link></li>
            <li><Link to="/packages" data-testid="footer-link-all">View all yatras</Link></li>
          </ul>
        </div>
        <div className="footer-col">
          <h5>2026 Campaigns</h5>
          <ul>
            <li><Link to="/l/char-dham-yatra-2026" data-testid="footer-link-lp-chardham">Char Dham Yatra 2026</Link></li>
            <li><Link to="/l/7-jyotirlinga-yatra-2026" data-testid="footer-link-lp-jyotirlinga">7 Jyotirlinga Yatra 2026</Link></li>
            <li><a href="/yatra#stories">Destinations</a></li>
            <li><a href="/yatra#why">Why us</a></li>
            <li><a href="/yatra#enquiry">Contact us</a></li>
          </ul>
        </div>
        <div className="footer-col">
          <h5>Contact</h5>
          <div className="footer-contact">
            <div className="footer-contact-item"><Phone size={16} /><a href={`tel:${PHONE}`} data-testid="footer-phone">+91 {PHONE}</a></div>
            <div className="footer-contact-item"><MessageCircle size={16} /><a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp">WhatsApp Chat</a></div>
            <div className="footer-contact-item"><Mail size={16} /><a href={`mailto:${EMAIL_PRIMARY}`} data-testid="footer-email">{EMAIL_PRIMARY}</a></div>
            <div className="footer-contact-item"><Mail size={16} /><a href={`mailto:${EMAIL_SECONDARY}`} data-testid="footer-email-secondary">{EMAIL_SECONDARY}</a></div>
            <div className="footer-contact-item" data-testid="footer-address">
              <MapPin size={16} />
              <span>
                {OFFICE_ADDRESS_LINES.map((l, i) => <span key={i} style={{ display: 'block' }}>{l}</span>)}
              </span>
            </div>
            <div className="footer-contact-item"><Compass size={16} /><span>arthsetuuglobal.in</span></div>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 Arth Setuu Global. All rights reserved.</span>
        <div className="footer-bottom-links">
          <Link to="/privacy-policy">Privacy</Link>
          <Link to="/terms-and-conditions">Terms</Link>
          <Link to="/refund-cancellation-policy">Refunds</Link>
          <Link to="/disclaimer">Disclaimer</Link>
          <Link to="/admin" data-testid="footer-team-login">Team login</Link>
        </div>
      </div>
    </footer>
  );
}

function Home() {
  const packages = usePackages();
  return (
    <>
      <SEO
        title="Arth Setuu Global — Premium Spiritual & Pilgrimage Journeys | India, Nepal & Beyond"
        description="Discover curated spiritual, pilgrimage and heritage yatras — Char Dham, 7 Jyotirlinga, Rameswaram, Nepal & more. Comfortable travel, transparent pricing, trusted advisors."
        keywords="spiritual tourism india, pilgrimage packages, char dham yatra, jyotirlinga yatra, rameswaram package, nepal yatra, arth setuu global, sacred journeys, yatra booking"
        canonical={`${SITE_URL}/`}
        schema={organizationSchema()}
      />
      <Header />
      <main>
        <Hero />
        <IntroBand />
        <DeitySection />
        <FeaturedYatras packages={packages} />
        <Departures packages={packages} />
        <Stories />
        <WhyUs />
        <Testimonials />
        <EnquirySection />
        <FinalCTA />
      </main>
      <Footer />
      <StickyCTAs />
    </>
  );
}

function Packages() {
  const packages = usePackages();
  const [filter, setFilter] = useState("all");
  const categories = ["all", ...new Set(packages.map(p => p.category?.toLowerCase()))];
  const filtered = filter === "all" ? packages : packages.filter(p => p.category?.toLowerCase() === filter);
  return (
    <>
      <SEO
        title="Sacred Yatras & Pilgrimage Packages | Arth Setuu Global"
        description="Explore all curated spiritual journeys — Char Dham, 7 Jyotirlinga, Rameswaram, Ganga Sagar, Nepal, Rishikesh-Haridwar and more. Compare durations, next departures and pricing."
        keywords="yatra packages, pilgrimage tour packages, spiritual tour india, sacred journey booking"
        canonical={`${SITE_URL}/packages`}
      />
      <Header />
      <main data-testid="packages-page">
        <div className="page-hero">
          <span className="eyebrow"><Sparkles size={13} /> The Collection</span>
          <h1>Find your <em className="italic-accent" style={{ color: 'var(--marigold)' }}>way inward.</em></h1>
          <p>Explore journeys across India, Nepal and the Himalayas — meaningful routes shaped by devotion and care.</p>
        </div>
        <div className="catalogue">
          <div className="filter-bar">
            <span className="filter-count" data-testid="filter-count">{filtered.length} sacred journeys</span>
            <div className="filter-chips">
              {categories.map(c => (
                <button key={c} onClick={() => setFilter(c)}
                  className={`chip ${filter === c ? "active" : ""}`}
                  data-testid={`filter-chip-${c}`}>
                  {c === "all" ? "All Yatras" : c.charAt(0).toUpperCase() + c.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="catalogue-grid">
            {filtered.map(p => <YatraCard key={p.slug} pkg={p} />)}
          </div>
        </div>
      </main>
      <Footer />
      <StickyCTAs />
    </>
  );
}

function PackagePage() {
  const { slug } = useParams();
  const packages = usePackages();
  const pkg = packages.find(p => p.slug === slug) || packages[0];
  if (!pkg) return null;
  const nextDep = pkg.departures?.[0]?.date || "Details to be confirmed";
  const tripSchema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": pkg.title,
    "description": pkg.short_description,
    "image": pkg.image,
    "touristType": ["Pilgrim", "Spiritual Traveler"],
    "itinerary": { "@type": "ItemList", "itemListElement": (pkg.destinations || []).map((d, i) => ({ "@type": "ListItem", "position": i + 1, "name": d })) },
    "provider": { "@type": "TravelAgency", "name": "Arth Setuu Global", "url": "https://arthsetuuglobal.in" }
  };
  return (
    <>
      <SEO
        title={`${pkg.title} — ${pkg.duration} | Arth Setuu Global`}
        description={`${pkg.short_description} Starting from ${pkg.price}. Next departure ${nextDep}. Book with Arth Setuu Global.`}
        image={pkg.image}
        canonical={`${SITE_URL}/yatra/${pkg.slug}`}
        keywords={`${pkg.title.toLowerCase()}, ${pkg.category?.toLowerCase()} yatra, ${pkg.destination?.toLowerCase()} pilgrimage, ${pkg.slug}`}
        schema={[organizationSchema(), tripSchema]}
      />
      <Header />
      <main data-testid="package-page">
        <div className="package-hero" style={{ backgroundImage: `url(${pkg.image})` }}>
          <div className="package-hero-content">
            <span className="eyebrow"><MapPin size={13} /> {pkg.category} · {pkg.destination}</span>
            <h1>{pkg.title}</h1>
            <p>{pkg.short_description}</p>
            <div className="package-hero-meta">
              <div><b>{pkg.duration}</b><small>Duration</small></div>
              <div><b>{pkg.price}</b><small>Starting from</small></div>
              <div><b>{nextDep}</b><small>Next Departure</small></div>
              <div><b>Group</b><small>Travel Style</small></div>
            </div>
            <CountdownBlock dateStr={nextDep} testid={`pkg-countdown-${pkg.slug}`} />
          </div>
        </div>
        <div className="package-body">
          <article>
            <span className="eyebrow"><Sparkles size={13} /> The Experience</span>
            <h2>A route with room for <em className="italic-accent">meaning.</em></h2>
            <p className="package-lead">
              {pkg.full_description || pkg.short_description} We keep the journey clear, respectful
              and comfortable, with practical information you can trust as you prepare.
            </p>

            <div className="package-section">
              <h3>What to expect</h3>
              <ul className="check-list">
                <li><Check size={17} /> Clear departure and travel information</li>
                <li><Check size={17} /> Dedicated advisor from planning to darshan</li>
                <li><Check size={17} /> Respectful pacing through sacred destinations</li>
                <li><Check size={17} /> Comfortable accommodation and reservation coaches</li>
                <li><Check size={17} /> Transparent inclusions and exclusions</li>
              </ul>
            </div>

            <div className="package-section">
              <h3>Important travel information</h3>
              <p style={{ color: 'var(--ink-soft)' }}>
                Circumstances such as weather, railway delays, road closures, government or temple
                restrictions and administrative decisions may require operational adjustments.
                Final itinerary details are shared closer to departure.
              </p>
            </div>
          </article>

          <aside className="package-sidebar">
            <div className="sidebar-card">
              <div className="sidebar-price">
                <small>Starting from</small>
                {pkg.price}
              </div>
              <div style={{ marginTop: '1.2rem', display: 'grid', gap: '0.5rem', fontSize: '0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Duration</span><b>{pkg.duration}</b></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Next departure</span><b>{nextDep}</b></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Category</span><b>{pkg.category}</b></div>
              </div>
              <div className="sidebar-cta-row">
                <a
                  className="btn btn-whatsapp"
                  href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20am%20interested%20in%20${encodeURIComponent(pkg.title)}`}
                  target="_blank" rel="noreferrer" data-testid="package-whatsapp"
                >
                  <MessageCircle size={16} /> Book on WhatsApp
                </a>
                <a className="btn btn-outline" href={`tel:${PHONE}`} data-testid="package-call">
                  <Phone size={16} /> Call +91 {PHONE}
                </a>
              </div>
            </div>
            <EnquiryForm selected={pkg.title} />
          </aside>
        </div>
      </main>
      <Footer />
      <StickyCTAs />
    </>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("asg_token", r.data.token);
      if (r.data.must_change_password) localStorage.setItem("asg_must_change", "1");
      onLogin(r.data.user);
    } catch { setError("Please check your sign-in details."); }
    finally { setBusy(false); }
  };
  return (
    <div className="login-page" data-testid="login-page">
      {showForgot ? (
        <ForgotPasswordForm onClose={() => setShowForgot(false)} />
      ) : (
        <form className="login-box" onSubmit={submit} autoComplete="off">
          <BrandLockup />
          <span className="eyebrow" style={{ justifyContent: 'center' }}>Team Access</span>
          <h1>Welcome back.</h1>
          <p>Sign in to manage yatras, careers and loan enquiries.</p>
          <label className="form-label">Email
            <input value={email} onChange={e => setEmail(e.target.value)} autoComplete="username"
                   placeholder="yatra@arthsetuuglobal.in" data-testid="login-email" required />
          </label>
          <label className="form-label">Password
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                   autoComplete="current-password" placeholder="Enter your password" data-testid="login-password" required />
          </label>
          {error && <div className="form-error" data-testid="login-error">{error}</div>}
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="login-submit">
            {busy ? "Signing in…" : <>Enter Workspace <ArrowRight size={16} /></>}
          </button>
          <button type="button" className="link-btn" onClick={() => setShowForgot(true)} data-testid="login-forgot">
            Forgot your password?
          </button>
        </form>
      )}
    </div>
  );
}

function ForgotPasswordForm({ onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, { email });
      setMsg(r.data.message || "If the account exists, a reset link has been sent.");
    } catch { setMsg("Could not send the reset link. Please try again shortly."); }
    finally { setBusy(false); }
  };
  return (
    <form className="login-box" onSubmit={submit} data-testid="forgot-password-form" autoComplete="off">
      <BrandLockup />
      <span className="eyebrow" style={{ justifyContent: 'center' }}>Reset password</span>
      <h1>Forgot your password?</h1>
      <p>Enter your account email. If it exists, a time-limited reset link will be sent to the recovery inbox <b>arthsetuuglobal@gmail.com</b>.</p>
      <label className="form-label">Account email
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
               placeholder="yatra@arthsetuuglobal.in" required data-testid="forgot-email" />
      </label>
      {msg && <div className="login-hint" data-testid="forgot-message" style={{color:"#166534"}}>{msg}</div>}
      <button type="submit" className="btn btn-primary" disabled={busy} data-testid="forgot-submit">
        {busy ? "Sending…" : "Send reset link"}
      </button>
      <button type="button" className="link-btn" onClick={onClose} data-testid="forgot-back">← Back to sign-in</button>
    </form>
  );
}

function ResetPasswordPage() {
  const [params] = useState(() => new URLSearchParams(window.location.search));
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setMsg("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { setErr("Passwords don't match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true); setMsg("Password updated. You can sign in with your new password.");
    } catch (ex) { setErr(ex.response?.data?.detail || "Could not reset password"); }
    finally { setBusy(false); }
  };
  return (
    <div className="login-page" data-testid="reset-page">
      <form className="login-box" onSubmit={submit} autoComplete="off">
        <BrandLockup />
        <span className="eyebrow" style={{ justifyContent: 'center' }}>Set new password</span>
        <h1>{done ? "All set." : "Create your new password."}</h1>
        {!token && <div className="form-error" data-testid="reset-no-token">Missing reset token. Please click the link from your reset email again.</div>}
        {done ? (
          <>
            <p>{msg}</p>
            <a className="btn btn-primary" href="/admin" data-testid="reset-back-login">Continue to sign-in</a>
          </>
        ) : (
          <>
            <label className="form-label">New password
              <input type="password" value={pw} onChange={e => setPw(e.target.value)}
                     minLength="8" required data-testid="reset-password" autoComplete="new-password" />
            </label>
            <label className="form-label">Confirm password
              <input type="password" value={pw2} onChange={e => setPw2(e.target.value)}
                     minLength="8" required data-testid="reset-password-confirm" autoComplete="new-password" />
            </label>
            {err && <div className="form-error" data-testid="reset-error">{err}</div>}
            <button type="submit" className="btn btn-primary" disabled={busy || !token} data-testid="reset-submit">
              {busy ? "Saving…" : "Set new password"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("crm");
  const [leads, setLeads] = useState([]);
  const [packages, setPackages] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [pkgEdit, setPkgEdit] = useState(null); // package being edited (or "new")
  const [quoteEdit, setQuoteEdit] = useState(null); // quotation being created
  const [landingPages, setLandingPages] = useState([]);
  const [landingEdit, setLandingEdit] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobApps, setJobApps] = useState([]);
  const [jobEdit, setJobEdit] = useState(null); // job being created or edited
  const [loans, setLoans] = useState([]);
  const [loanApps, setLoanApps] = useState([]);

  const token = () => localStorage.getItem("asg_token");
  const authH = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const loadAll = async () => {
    if (!token()) return;
    try {
      const [me, lds, pks, qts, lp, jb, ja, ln, la] = await Promise.all([
        axios.get(`${API}/auth/me`, authH()),
        axios.get(`${API}/admin/leads`, authH()),
        axios.get(`${API}/admin/packages`, authH()),
        axios.get(`${API}/admin/quotations`, authH()),
        axios.get(`${API}/admin/landing`, authH()),
        axios.get(`${API}/admin/jobs`, authH()).catch(() => ({data:[]})),
        axios.get(`${API}/admin/job_applications`, authH()).catch(() => ({data:[]})),
        axios.get(`${API}/admin/loans`, authH()).catch(() => ({data:[]})),
        axios.get(`${API}/admin/loan_applications`, authH()).catch(() => ({data:[]})),
      ]);
      setUser(me.data); setLeads(lds.data); setPackages(pks.data); setQuotations(qts.data); setLandingPages(lp.data);
      setJobs(jb.data); setJobApps(ja.data); setLoans(ln.data); setLoanApps(la.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (user) loadAll(); /* refetch when switching tabs so newly-added data appears */ }, [tab]);

  if (!user) return <Login onLogin={() => loadAll()} />;

  const tabs = [
    ["crm", "CRM"],
    ["sync", "Sync"],
    ["overview", "Overview"],
    ["packages", "Packages"],
    ["landing", "SEO Pages"],
    ["quotations", "Quotations"],
    ["leads", "Yatra Leads"],
    ["departures", "Departures"],
    ["jobs", "Jobs"],
    ["job_applications", "Job Applications"],
    ["loans", "Loans"],
    ["loan_applications", "Loan Enquiries"],
  ];

  const deletePkg = async (id) => {
    if (!window.confirm("Delete this package? This cannot be undone.")) return;
    await axios.delete(`${API}/admin/packages/${id}`, authH());
    loadAll();
  };

  const deleteLanding = async (id) => {
    if (!window.confirm("Delete this landing page? This cannot be undone.")) return;
    await axios.delete(`${API}/admin/landing/${id}`, authH());
    loadAll();
  };

  const togglePublish = async (p) => {
    await axios.patch(`${API}/admin/packages/${p.id}`, { ...p, active: !p.active }, authH());
    loadAll();
  };

  return (
    <div className="admin" data-testid="admin-page">
      <aside className="admin-rail">
        <BrandLockup dark />
        <span className="eyebrow">Workspace</span>
        <div className="admin-nav">
          {tabs.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`admin-nav-btn ${tab === id ? "active" : ""}`}
              data-testid={`admin-nav-${id}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="admin-rail-footer">
          <b>{user.name || user.email}</b>
          <small>{user.role}</small>
          <button
            className="admin-logout"
            onClick={() => { localStorage.removeItem("asg_token"); setUser(null); }}
            data-testid="admin-logout"
          >Sign out</button>
        </div>
      </aside>
      <section className="admin-main">
        <div className="admin-top">
          <div>
            <span className="eyebrow">Namaste, {(user.name || user.email).split(" ")[0]}</span>
            <h1>{tab === "crm" ? "CRM" : tab === "sync" ? "Google Sheets Sync" : tab.replace(/_/g, " ")}</h1>
          </div>
          <Link to="/" className="link-arrow" data-testid="admin-view-site">View public site <ArrowRight size={14} /></Link>
        </div>

        {tab === "crm" && <CRMTab />}
        {tab === "sync" && <SyncTab />}

        {tab === "overview" && (
          <>
            <div className="admin-metrics">
              <div className="admin-metric" data-testid="metric-journeys"><span>Active journeys</span><strong>{packages.filter(p => p.active !== false).length}</strong><small>Live catalogue</small></div>
              <div className="admin-metric" data-testid="metric-new-leads"><span>New enquiries</span><strong>{leads.filter(l => l.status === "New").length}</strong><small>Unassigned</small></div>
              <div className="admin-metric" data-testid="metric-quotations"><span>Quotations</span><strong>{quotations.length}</strong><small>Total sent</small></div>
              <div className="admin-metric" data-testid="metric-conversion"><span>Conversion</span><strong>—</strong><small>This month</small></div>
            </div>
            <div className="admin-panel">
              <div className="panel-heading">
                <h2>Recent enquiries</h2>
                <button className="link-arrow" onClick={() => setTab("leads")} data-testid="view-all-leads">
                  View all <ArrowRight size={14} />
                </button>
              </div>
              {leads.length ? <LeadRows leads={leads.slice(0, 5)} /> : (
                <div className="panel-empty" data-testid="empty-leads">Website enquiries will appear here.</div>
              )}
            </div>
          </>
        )}

        {tab === "packages" && (
          <div className="admin-panel">
            <div className="panel-heading">
              <h2>Journey catalogue</h2>
              <button className="btn btn-primary btn-sm" data-testid="admin-add-package"
                onClick={() => setPkgEdit(EMPTY_PKG)}>
                Add package <ArrowRight size={14} />
              </button>
            </div>
            <div className="data-list package-admin-list">
              {packages.map(p => (
                <div key={p.id || p.slug} data-testid={`admin-package-${p.slug}`}>
                  <img src={p.image} alt={p.title} />
                  <div><b>{p.title}</b><small>{p.category} · {p.duration} · {p.destination}</small></div>
                  <em className={`status-badge ${p.active !== false ? "published" : ""}`}
                    onClick={() => togglePublish(p)} style={{ cursor: 'pointer' }}
                    data-testid={`toggle-publish-${p.slug}`}>
                    {p.active !== false ? "Published" : "Draft"}
                  </em>
                  <b style={{ color: 'var(--saffron)' }}>{p.price}</b>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button className="icon-btn" onClick={() => setPkgEdit(p)} data-testid={`edit-pkg-${p.slug}`} title="Edit"><Pencil size={15} /></button>
                    <button className="icon-btn danger" onClick={() => deletePkg(p.id)} data-testid={`delete-pkg-${p.slug}`} title="Delete"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
              {packages.length === 0 && <div className="panel-empty">No packages yet. Click Add package to create your first yatra.</div>}
            </div>
          </div>
        )}

        {tab === "quotations" && (
          <div className="admin-panel">
            <div className="panel-heading">
              <h2>Quotations</h2>
              <button className="btn btn-primary btn-sm" data-testid="admin-add-quotation"
                onClick={() => setQuoteEdit(EMPTY_QUOTE)}>
                New quotation <ArrowRight size={14} />
              </button>
            </div>
            <div className="data-list quotation-list">
              {quotations.map(q => (
                <div key={q.id} data-testid={`quotation-row-${q.id}`}>
                  <div className="data-avatar" style={{ background: 'linear-gradient(135deg, var(--maroon), var(--saffron))' }}>
                    <FileText size={17} />
                  </div>
                  <div>
                    <b>{q.number} · {q.customer_name}</b>
                    <small>{q.package} · {q.customer_mobile} · {q.adults}A {q.children ? `${q.children}C` : ""}</small>
                  </div>
                  <em className="status-badge">{q.status || "Draft"}</em>
                  <b style={{ color: 'var(--saffron)' }}>₹{Number(q.grand_total).toLocaleString('en-IN')}</b>
                  <a
                    href={`${API}/admin/quotations/${q.id}/pdf?token=${token()}`}
                    target="_blank" rel="noreferrer"
                    className="btn btn-outline btn-sm"
                    data-testid={`download-pdf-${q.id}`}
                  >
                    <Download size={13} /> PDF
                  </a>
                </div>
              ))}
              {quotations.length === 0 && <div className="panel-empty">No quotations yet. Click New quotation to create one.</div>}
            </div>
          </div>
        )}

        {tab === "landing" && (
          <div className="admin-panel">
            <div className="panel-heading">
              <h2>SEO landing pages</h2>
              <button className="btn btn-primary btn-sm" data-testid="admin-add-landing"
                onClick={() => setLandingEdit(EMPTY_LANDING)}>
                Add landing page <ArrowRight size={14} />
              </button>
            </div>
            <div className="data-list quotation-list">
              {landingPages.map(lp => (
                <div key={lp.id} data-testid={`landing-row-${lp.slug}`}>
                  <div className="data-avatar" style={{ background: 'linear-gradient(135deg, var(--gold), var(--saffron))' }}>
                    <Globe size={17} />
                  </div>
                  <div>
                    <b>{lp.title}</b>
                    <small>/l/{lp.slug} · {lp.meta_title?.slice(0, 60)}</small>
                  </div>
                  <em className={`status-badge ${lp.active ? "published" : ""}`}>
                    {lp.active ? "Live" : "Draft"}
                  </em>
                  <a
                    href={`/l/${lp.slug}`} target="_blank" rel="noreferrer"
                    className="btn btn-outline btn-sm"
                    data-testid={`view-landing-${lp.slug}`}
                  >
                    View
                  </a>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button className="icon-btn" onClick={() => setLandingEdit(lp)} data-testid={`edit-landing-${lp.slug}`} title="Edit"><Pencil size={15} /></button>
                    <button className="icon-btn danger" onClick={() => deleteLanding(lp.id)} data-testid={`delete-landing-${lp.slug}`} title="Delete"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
              {landingPages.length === 0 && <div className="panel-empty">No landing pages yet. Click Add landing page to build one for SEO campaigns.</div>}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="admin-panel">
            <div className="panel-heading">
              <h2>Lead conversations</h2>
              <button className="btn btn-primary btn-sm" data-testid="admin-add-lead"
                onClick={() => setQuoteEdit(EMPTY_QUOTE)}>
                Create quotation <ArrowRight size={14} />
              </button>
            </div>
            {leads.length ? <LeadRows leads={leads} /> : (
              <div className="panel-empty" data-testid="empty-leads-full">No leads yet — enquiries from the website will land here.</div>
            )}
          </div>
        )}

        {tab === "departures" && (
          <div className="admin-panel">
            <div className="panel-heading"><h2>Upcoming departures</h2></div>
            <div className="data-list">
              {packages.flatMap(p => (p.departures || []).map(d => (
                <div key={p.slug + d.date} data-testid={`admin-departure-${p.slug}`}>
                  <div className="data-avatar"><CalendarDays size={17} /></div>
                  <div><b>{p.title}</b><small>{d.date} · {d.label}</small></div>
                  <em className="status-badge">{p.duration}</em>
                  <span>{p.destination}</span>
                  <b style={{ color: 'var(--saffron)' }}>{p.price}</b>
                </div>
              )))}
            </div>
          </div>
        )}

        {tab === "jobs" && (
          <div className="admin-panel asg-admin-panel">
            <div className="panel-heading">
              <h2>Manage jobs ({jobs.length})</h2>
              <button className="btn btn-primary btn-sm" data-testid="admin-add-job"
                onClick={() => setJobEdit({
                  title:"", slug:"", category:"General", location:"Danapur, Patna",
                  employment_type:"Full-time", experience:"Fresher / 0-2 years", salary_range:"As per industry",
                  short_description:"", full_description:"",
                  requirements:[], responsibilities:[], benefits:[],
                  posted_at: new Date().toISOString().slice(0,10), active:true
                })}
              >Post new job <ArrowRight size={14} /></button>
            </div>
            <table data-testid="admin-jobs-table">
              <thead><tr><th>Title</th><th>Category</th><th>Location</th><th>Type</th><th>Posted</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {jobs.map(j => (
                  <tr key={j.id} data-testid={`admin-job-${j.slug}`}>
                    <td><b>{j.title}</b><br/><small style={{color:'#64748B'}}>{j.slug}</small></td>
                    <td>{j.category}</td>
                    <td>{j.location}</td>
                    <td>{j.employment_type}</td>
                    <td><small>{j.posted_at || (j.created_at || "").slice(0,10) || "—"}</small></td>
                    <td>{j.active ? "Live" : "Draft"}</td>
                    <td>
                      <a className="asg-mini-btn" href={`/jobs/${j.slug}`} target="_blank" rel="noreferrer">View</a>
                      <button className="asg-mini-btn"
                        onClick={() => setJobEdit({ ...j, requirements: j.requirements||[], responsibilities: j.responsibilities||[], benefits: j.benefits||[] })}
                        data-testid={`admin-job-edit-${j.slug}`}
                      >Edit</button>
                      <button className="asg-mini-btn"
                        onClick={async () => { await axios.patch(`${API}/admin/jobs/${j.id}`, { ...j, active: !j.active }, authH()); loadAll(); }}
                        data-testid={`admin-job-toggle-${j.slug}`}
                      >{j.active ? "Pause" : "Publish"}</button>
                      <button className="asg-mini-btn danger"
                        onClick={async () => { if (window.confirm("Delete this job?")) { await axios.delete(`${API}/admin/jobs/${j.id}`, authH()); loadAll(); } }}
                        data-testid={`admin-job-delete-${j.slug}`}
                      >Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "job_applications" && (
          <div className="admin-panel asg-admin-panel">
            <div className="panel-heading"><h2>Job applications ({jobApps.length})</h2></div>
            <table data-testid="admin-job-apps-table">
              <thead><tr><th>ID</th><th>Applicant</th><th>Role</th><th>Contact</th><th>Received</th><th>Resume</th></tr></thead>
              <tbody>
                {jobApps.map(a => (
                  <tr key={a.id} data-testid={`admin-job-app-${a.id}`}>
                    <td><small>{a.id}</small></td>
                    <td><b>{a.name}</b><br/><small style={{color:'#64748B'}}>{a.city || "—"}</small></td>
                    <td>{a.job_title}</td>
                    <td>{a.mobile}<br/><small>{a.email || "—"}</small></td>
                    <td><small>{new Date(a.created_at).toLocaleString()}</small></td>
                    <td>
                      {a.resume_path ? (
                        <a className="asg-mini-btn"
                          href={`${API}/admin/resume/${a.id}?token=${token()}`}
                          target="_blank" rel="noreferrer"
                          data-testid={`admin-job-resume-${a.id}`}
                        >Download</a>
                      ) : <small style={{color:'#94a3b8'}}>—</small>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {jobApps.length === 0 && <div className="panel-empty" data-testid="empty-job-apps">No job applications yet.</div>}
          </div>
        )}

        {tab === "loans" && (
          <div className="admin-panel asg-admin-panel">
            <div className="panel-heading"><h2>Manage loan products ({loans.length})</h2></div>
            <table data-testid="admin-loans-table">
              <thead><tr><th>Product</th><th>Category</th><th>Interest</th><th>Tenure</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {loans.map(l => (
                  <tr key={l.id} data-testid={`admin-loan-${l.slug}`}>
                    <td><b>{l.icon} {l.name}</b><br/><small style={{color:'#64748B'}}>{l.slug}</small></td>
                    <td>{l.category}</td>
                    <td>{l.interest_range}</td>
                    <td>{l.tenure_range}</td>
                    <td>{l.active ? "Live" : "Draft"}</td>
                    <td>
                      <a className="asg-mini-btn" href={`/loans/${l.slug}`} target="_blank" rel="noreferrer">View</a>
                      <button className="asg-mini-btn"
                        onClick={async () => { await axios.patch(`${API}/admin/loans/${l.id}`, { ...l, active: !l.active }, authH()); loadAll(); }}
                        data-testid={`admin-loan-toggle-${l.slug}`}
                      >{l.active ? "Pause" : "Publish"}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "loan_applications" && (
          <div className="admin-panel asg-admin-panel">
            <div className="panel-heading"><h2>Loan enquiries ({loanApps.length})</h2></div>
            <table data-testid="admin-loan-apps-table">
              <thead><tr><th>ID</th><th>Applicant</th><th>Loan</th><th>Amount / Income</th><th>Contact</th><th>Received</th></tr></thead>
              <tbody>
                {loanApps.map(a => (
                  <tr key={a.id} data-testid={`admin-loan-app-${a.id}`}>
                    <td><small>{a.id}</small></td>
                    <td><b>{a.name}</b><br/><small style={{color:'#64748B'}}>{a.city || "—"} · {a.occupation || "—"}</small></td>
                    <td>{a.loan_name}</td>
                    <td>{a.loan_amount || "—"}<br/><small style={{color:'#64748B'}}>{a.monthly_income || "—"}/mo</small></td>
                    <td>{a.mobile}<br/><small>{a.email || "—"}</small></td>
                    <td><small>{new Date(a.created_at).toLocaleString()}</small></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loanApps.length === 0 && <div className="panel-empty" data-testid="empty-loan-apps">No loan enquiries yet.</div>}
          </div>
        )}
      </section>

      {pkgEdit && (
        <PackageEditor pkg={pkgEdit} onClose={() => setPkgEdit(null)} onSaved={() => { setPkgEdit(null); loadAll(); }} authH={authH} />
      )}
      {quoteEdit && (
        <QuotationEditor packages={packages} onClose={() => setQuoteEdit(null)} onSaved={() => { setQuoteEdit(null); loadAll(); }} authH={authH} tokenFn={token} />
      )}
      {landingEdit && (
        <LandingEditor page={landingEdit} packages={packages} onClose={() => setLandingEdit(null)} onSaved={() => { setLandingEdit(null); loadAll(); }} authH={authH} />
      )}
      {jobEdit && (
        <JobEditor job={jobEdit} onClose={() => setJobEdit(null)} onSaved={() => { setJobEdit(null); loadAll(); }} authH={authH} />
      )}
    </div>
  );
}

const EMPTY_LANDING = {
  slug: "", title: "", subheadline: "", hero_image: "",
  meta_title: "", meta_description: "", keywords: "",
  body_intro: "", highlights: [], featured_slugs: [], faqs: [],
  cta_title: "Ready to plan this journey?",
  cta_text: "Speak to a journey advisor today for dates, pricing and personal guidance.",
  canonical_url: "", og_image: "", active: true,
};

const EMPTY_PKG = {
  title: "", slug: "", category: "Shiva", destination: "",
  duration: "", price: "", image: "", short_description: "",
  full_description: "", featured: false, active: true,
  departures: [], destinations: []
};

const EMPTY_QUOTE = {
  customer_name: "", customer_mobile: "", customer_email: "", customer_city: "",
  package: "", departure: "", travel_class: "Standard",
  adults: 2, children: 0,
  base_price: 0, transport: 0, hotel: 0, other_charges: 0,
  discount: 0, advance: 0, gst_percent: 5, validity_days: 15,
  inclusions: [], exclusions: [], notes: ""
};

function PackageEditor({ pkg, onClose, onSaved, authH }) {
  const isNew = !pkg.id;
  const [form, setForm] = useState({ ...EMPTY_PKG, ...pkg });
  const [depDate, setDepDate] = useState("");
  const [depLabel, setDepLabel] = useState("");
  const [destStr, setDestStr] = useState((pkg.destinations || []).join(", "));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const addDeparture = () => {
    if (!depDate) return;
    set("departures", [...(form.departures || []), { date: depDate, label: depLabel || "Departure" }]);
    setDepDate(""); setDepLabel("");
  };
  const removeDeparture = (i) => set("departures", form.departures.filter((_, idx) => idx !== i));

  const save = async () => {
    setError(""); setBusy(true);
    const payload = { ...form, destinations: destStr.split(",").map(s => s.trim()).filter(Boolean) };
    if (!payload.title || !payload.slug) { setError("Title and slug are required."); setBusy(false); return; }
    try {
      if (isNew) await axios.post(`${API}/admin/packages`, payload, authH());
      else await axios.patch(`${API}/admin/packages/${pkg.id}`, payload, authH());
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="package-editor-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{isNew ? "Create" : "Edit"} package</span>
            <h2>{isNew ? "Add a new yatra" : form.title}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-package-editor"><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <label className="form-label">Title
              <input value={form.title} onChange={e => set("title", e.target.value)} data-testid="pkg-title" />
            </label>
            <label className="form-label">Slug (URL)
              <input value={form.slug} onChange={e => set("slug", e.target.value)}
                placeholder="e.g. 7-jyotirlinga-yatra" data-testid="pkg-slug" />
            </label>
          </div>
          <div className="form-grid">
            <label className="form-label">Category
              <select value={form.category} onChange={e => set("category", e.target.value)} data-testid="pkg-category">
                {["Shiva", "Shakti", "Vishnu", "Himalayan", "Dham Yatras", "Heritage", "Buddhist"].map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="form-label">Destination
              <input value={form.destination} onChange={e => set("destination", e.target.value)} data-testid="pkg-destination" />
            </label>
          </div>
          <div className="form-grid">
            <label className="form-label">Duration
              <input value={form.duration} onChange={e => set("duration", e.target.value)}
                placeholder="e.g. 11 days / 10 nights" data-testid="pkg-duration" />
            </label>
            <label className="form-label">Starting price
              <input value={form.price} onChange={e => set("price", e.target.value)}
                placeholder="e.g. ₹18,500" data-testid="pkg-price" />
            </label>
          </div>
          <label className="form-label">Hero image
            <input value={form.image} onChange={e => set("image", e.target.value)}
              placeholder="Paste URL or use upload below" data-testid="pkg-image" />
            <div className="pkg-img-uploader" data-testid="pkg-image-uploader">
              <input type="file" accept=".jpg,.jpeg,.png,.webp" style={{ display: "none" }}
                id="pkg-image-file" data-testid="pkg-image-file"
                onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  set("image", "uploading:" + file.name);
                  const fd = new FormData(); fd.append("kind", "package_image"); fd.append("file", file);
                  try {
                    const r = await axios.post(`${API}/admin/upload`, fd, {
                      ...authH(), headers: { ...authH().headers, "Content-Type": "multipart/form-data" }
                    });
                    const backend = process.env.REACT_APP_BACKEND_URL || "";
                    set("image", backend + r.data.url);
                  } catch (er) {
                    setError(er.response?.data?.detail || "Image upload failed");
                    set("image", "");
                  }
                  e.target.value = "";
                }} />
              <label htmlFor="pkg-image-file" className="pkg-img-btn" data-testid="pkg-image-upload-btn">
                Upload from device (mobile / desktop)
              </label>
              {form.image && !form.image.startsWith("uploading:") && (
                <img src={form.image} alt="preview" className="pkg-img-preview" data-testid="pkg-image-preview"
                     onError={(e) => { e.target.style.display = "none"; }} />
              )}
              {form.image?.startsWith("uploading:") && <small style={{ color: "#64748B" }}>Uploading {form.image.slice(10)}…</small>}
              {form.image && !form.image.startsWith("uploading:") && (
                <button type="button" className="pkg-img-clear" data-testid="pkg-image-clear" onClick={() => set("image", "")}>Remove image</button>
              )}
            </div>
          </label>
          <label className="form-label">Key destinations (comma-separated)
            <input value={destStr} onChange={e => setDestStr(e.target.value)}
              placeholder="e.g. Somnath, Trimbakeshwar, Bhimashankar" data-testid="pkg-destinations" />
          </label>
          <label className="form-label">Short description
            <textarea value={form.short_description} onChange={e => set("short_description", e.target.value)} data-testid="pkg-short" />
          </label>
          <label className="form-label">Full description (shown on package page)
            <textarea value={form.full_description} onChange={e => set("full_description", e.target.value)}
              style={{ minHeight: 140 }} data-testid="pkg-full" />
          </label>

          <div className="form-label">Departures
            <div className="departure-editor">
              {(form.departures || []).map((d, i) => (
                <div key={i} className="dep-chip">
                  <b>{d.date}</b><small>{d.label}</small>
                  <button className="icon-btn danger" onClick={() => removeDeparture(i)}><X size={12} /></button>
                </div>
              ))}
              <div className="dep-add">
                <input placeholder="Date (e.g. 29 Sep 2026)" value={depDate} onChange={e => setDepDate(e.target.value)} data-testid="dep-date" />
                <input placeholder="Label" value={depLabel} onChange={e => setDepLabel(e.target.value)} data-testid="dep-label" />
                <button className="btn btn-outline btn-sm" onClick={addDeparture} data-testid="add-dep">+ Add</button>
              </div>
            </div>
          </div>

          <div className="form-grid" style={{ marginTop: '0.6rem' }}>
            <label className="checkbox-row">
              <input type="checkbox" checked={!!form.featured} onChange={e => set("featured", e.target.checked)} data-testid="pkg-featured" />
              <span>Feature on homepage</span>
            </label>
            <label className="checkbox-row">
              <input type="checkbox" checked={form.active !== false} onChange={e => set("active", e.target.checked)} data-testid="pkg-active" />
              <span>Publish (visible on public site)</span>
            </label>
          </div>

          {error && <div className="form-error" data-testid="pkg-error">{error}</div>}
        </div>
        <div className="modal-foot">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="save-package">
            {busy ? "Saving..." : isNew ? "Create package" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuotationEditor({ packages, onClose, onSaved, authH, tokenFn }) {
  const [form, setForm] = useState(EMPTY_QUOTE);
  const [inclStr, setInclStr] = useState("");
  const [exclStr, setExclStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedQuote, setSavedQuote] = useState(null);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setNum = (k, v) => set(k, Number(v) || 0);

  // Auto-calc totals
  const perAdultBase = form.base_price;
  const childBase = form.base_price * 0.5;
  const subtotal = perAdultBase * form.adults + childBase * form.children + form.transport + form.hotel + form.other_charges;
  const gst = +(subtotal * form.gst_percent / 100).toFixed(2);
  const grand = +(subtotal + gst - form.discount).toFixed(2);
  const balance = +(grand - form.advance).toFixed(2);

  const save = async () => {
    setError(""); setBusy(true);
    try {
      const payload = {
        ...form,
        inclusions: inclStr.split("\n").map(s => s.trim()).filter(Boolean),
        exclusions: exclStr.split("\n").map(s => s.trim()).filter(Boolean),
      };
      const r = await axios.post(`${API}/admin/quotations`, payload, authH());
      setSavedQuote(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create quotation.");
    } finally { setBusy(false); }
  };

  if (savedQuote) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="quotation-success-modal">
          <div className="modal-head">
            <div>
              <span className="eyebrow">Quotation Created</span>
              <h2>{savedQuote.number}</h2>
            </div>
            <button className="icon-btn" onClick={() => { onSaved(); }}><X size={18} /></button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, var(--saffron), var(--maroon))', color: '#fff', display: 'grid', placeItems: 'center', margin: '0 auto 1.5rem' }}>
              <Check size={32} />
            </div>
            <h3 style={{ marginBottom: '0.5rem' }}>Quotation ready for {savedQuote.customer_name}</h3>
            <p style={{ color: 'var(--ink-soft)', marginBottom: '2rem' }}>Grand total: ₹{Number(savedQuote.grand_total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · Balance: ₹{Number(savedQuote.balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center' }}>
              <a
                className="btn btn-primary"
                href={`${API}/admin/quotations/${savedQuote.id}/pdf?token=${tokenFn()}`}
                target="_blank" rel="noreferrer"
                data-testid="download-quotation-pdf"
              >
                <Download size={16} /> Download PDF
              </a>
              <button className="btn btn-outline" onClick={onSaved}>Done</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="quotation-editor-modal">
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">Create Quotation</span>
            <h2>New quotation</h2>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <h4 className="editor-section-title">Customer</h4>
          <div className="form-grid">
            <label className="form-label">Name<input value={form.customer_name} onChange={e => set("customer_name", e.target.value)} data-testid="q-name" /></label>
            <label className="form-label">Mobile<input value={form.customer_mobile} onChange={e => set("customer_mobile", e.target.value)} data-testid="q-mobile" /></label>
          </div>
          <div className="form-grid">
            <label className="form-label">Email<input value={form.customer_email} onChange={e => set("customer_email", e.target.value)} data-testid="q-email" /></label>
            <label className="form-label">City<input value={form.customer_city} onChange={e => set("customer_city", e.target.value)} data-testid="q-city" /></label>
          </div>

          <h4 className="editor-section-title">Journey</h4>
          <div className="form-grid">
            <label className="form-label">Package
              <select value={form.package} onChange={e => set("package", e.target.value)} data-testid="q-package">
                <option value="">Select a yatra</option>
                {packages.map(p => <option key={p.slug} value={p.title}>{p.title}</option>)}
              </select>
            </label>
            <label className="form-label">Departure<input value={form.departure} onChange={e => set("departure", e.target.value)} placeholder="e.g. 29 Sep 2026" data-testid="q-departure" /></label>
          </div>
          <div className="form-grid">
            <label className="form-label">Travel class
              <select value={form.travel_class} onChange={e => set("travel_class", e.target.value)} data-testid="q-class">
                {["Standard", "Reservation Coach", "Deluxe", "Premium"].map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
            <div className="form-grid">
              <label className="form-label">Adults<input type="number" min="1" value={form.adults} onChange={e => setNum("adults", e.target.value)} data-testid="q-adults" /></label>
              <label className="form-label">Children<input type="number" min="0" value={form.children} onChange={e => setNum("children", e.target.value)} data-testid="q-children" /></label>
            </div>
          </div>

          <h4 className="editor-section-title">Pricing</h4>
          <div className="form-grid">
            <label className="form-label">Base price per adult (₹)<input type="number" value={form.base_price} onChange={e => setNum("base_price", e.target.value)} data-testid="q-base" /></label>
            <label className="form-label">Transport (₹)<input type="number" value={form.transport} onChange={e => setNum("transport", e.target.value)} data-testid="q-transport" /></label>
          </div>
          <div className="form-grid">
            <label className="form-label">Hotel / Accommodation (₹)<input type="number" value={form.hotel} onChange={e => setNum("hotel", e.target.value)} data-testid="q-hotel" /></label>
            <label className="form-label">Other charges (₹)<input type="number" value={form.other_charges} onChange={e => setNum("other_charges", e.target.value)} data-testid="q-other" /></label>
          </div>
          <div className="form-grid">
            <label className="form-label">GST %<input type="number" step="0.5" value={form.gst_percent} onChange={e => setNum("gst_percent", e.target.value)} data-testid="q-gst" /></label>
            <label className="form-label">Discount (₹)<input type="number" value={form.discount} onChange={e => setNum("discount", e.target.value)} data-testid="q-discount" /></label>
          </div>
          <div className="form-grid">
            <label className="form-label">Advance received (₹)<input type="number" value={form.advance} onChange={e => setNum("advance", e.target.value)} data-testid="q-advance" /></label>
            <label className="form-label">Validity (days)<input type="number" value={form.validity_days} onChange={e => setNum("validity_days", e.target.value)} data-testid="q-validity" /></label>
          </div>

          <div className="quote-summary" data-testid="quote-summary">
            <div><span>Subtotal</span><b>₹{subtotal.toLocaleString('en-IN')}</b></div>
            <div><span>GST @ {form.gst_percent}%</span><b>₹{gst.toLocaleString('en-IN')}</b></div>
            <div><span>Discount</span><b>- ₹{form.discount.toLocaleString('en-IN')}</b></div>
            <div className="grand"><span>Grand total</span><b>₹{grand.toLocaleString('en-IN')}</b></div>
            <div><span>Balance payable</span><b>₹{balance.toLocaleString('en-IN')}</b></div>
          </div>

          <h4 className="editor-section-title">Inclusions & Exclusions</h4>
          <div className="form-grid">
            <label className="form-label">Inclusions (one per line)
              <textarea value={inclStr} onChange={e => setInclStr(e.target.value)}
                placeholder="Rail reservation&#10;Hotel accommodation&#10;Daily breakfast" data-testid="q-inclusions" style={{ minHeight: 120 }} />
            </label>
            <label className="form-label">Exclusions (one per line)
              <textarea value={exclStr} onChange={e => setExclStr(e.target.value)}
                placeholder="Airfare&#10;Personal expenses" data-testid="q-exclusions" style={{ minHeight: 120 }} />
            </label>
          </div>
          <label className="form-label">Notes<textarea value={form.notes} onChange={e => set("notes", e.target.value)} data-testid="q-notes" /></label>

          {error && <div className="form-error" data-testid="q-error">{error}</div>}
        </div>
        <div className="modal-foot">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="save-quotation">
            {busy ? "Generating..." : <>Create & Generate PDF <FileText size={16} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadRows({ leads }) {
  return (
    <div className="data-list">
      {leads.map(l => (
        <div key={l.id} data-testid={`lead-row-${l.id}`}>
          <div className="data-avatar">{l.name?.[0]?.toUpperCase() || "L"}</div>
          <div><b>{l.name}</b><small>{l.package || "General enquiry"} · {l.mobile}</small></div>
          <em className={`status-badge ${l.status?.toLowerCase()}`}>{l.status}</em>
          <span>{l.city || "—"}</span>
          <time style={{ color: 'var(--ink-soft)', fontSize: '0.78rem' }}>
            {l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}
          </time>
        </div>
      ))}
    </div>
  );
}

function Legal({ title, kind }) {
  const content = {
    privacy: (
      <>
        <p>At Arth Setuu Global, your trust matters. This page explains how we handle your information when you enquire about our journeys.</p>
        <h2>Information we collect</h2>
        <p>When you submit an enquiry form, we collect your name, mobile, email, city and journey preferences. This information is used solely to respond to your travel request.</p>
        <h2>How we use it</h2>
        <p>Your details are stored in our internal CRM and used by our journey advisors to contact you regarding your yatra. We do not sell or share your data with third parties.</p>
        <h2>Consent</h2>
        <p>Consent is never pre-checked on our forms. You may withdraw consent or request deletion of your data by writing to {EMAIL_PRIMARY} or {EMAIL_SECONDARY}.</p>
      </>
    ),
    terms: (
      <>
        <p>These terms govern your use of the Arth Setuu Global website and services. By enquiring or booking with us, you accept these terms.</p>
        <h2>Journeys and information</h2>
        <p>We aim to keep pricing, departures and inclusions accurate. Circumstances may require operational adjustments — including weather, road/rail issues, government restrictions and temple administration decisions.</p>
        <h2>Bookings</h2>
        <p>Final journey details, pricing and cancellation terms are shared in your quotation. Bookings are confirmed upon receipt of the registration amount.</p>
      </>
    ),
    refund: (
      <>
        <p>Cancellation terms vary by yatra. Your quotation will include the specific cancellation schedule for your journey.</p>
        <h2>General framework</h2>
        <p>Cancellations closer to departure carry higher deductions. GST and third-party charges (train, hotel, temple) are as per operational rules. We aim for prompt refund processing where applicable.</p>
        <h2>How to cancel</h2>
        <p>Write to {EMAIL_PRIMARY} with your booking reference. Our team will confirm the applicable deduction and process the refund per policy.</p>
      </>
    ),
    disclaimer: (
      <>
        <p>Spiritual journeys involve travel to sacred, high-altitude and heritage destinations. Please read carefully.</p>
        <h2>Operational circumstances</h2>
        <p>Railway delays, road closures, landslides, weather events, natural disasters, government or temple restrictions can affect itineraries. We do not guarantee circumstances outside reasonable control.</p>
        <h2>Traveller responsibility</h2>
        <p>Please carry valid identification, medical prescriptions, and emergency contacts. For high-altitude yatras, consult a medical professional to assess fitness.</p>
      </>
    ),
  };
  return (
    <>
      <Header />
      <main className="legal" data-testid={`legal-page-${kind}`}>
        <span className="eyebrow"><ShieldCheck size={13} /> Arth Setuu Global</span>
        <h1>{title}</h1>
        {content[kind]}
      </main>
      <Footer />
      <StickyCTAs />
    </>
  );
}

function LandingEditor({ page, packages, onClose, onSaved, authH }) {
  const isNew = !page.id;
  const [form, setForm] = useState({ ...EMPTY_LANDING, ...page });
  const [highlightsStr, setHighlightsStr] = useState((page.highlights || []).join("\n"));
  const [featuredStr, setFeaturedStr] = useState((page.featured_slugs || []).join(", "));
  const [faqQ, setFaqQ] = useState("");
  const [faqA, setFaqA] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const addFaq = () => {
    if (!faqQ || !faqA) return;
    set("faqs", [...(form.faqs || []), { q: faqQ, a: faqA }]);
    setFaqQ(""); setFaqA("");
  };
  const removeFaq = (i) => set("faqs", form.faqs.filter((_, idx) => idx !== i));

  const save = async () => {
    setError(""); setBusy(true);
    const payload = {
      ...form,
      highlights: highlightsStr.split("\n").map(s => s.trim()).filter(Boolean),
      featured_slugs: featuredStr.split(",").map(s => s.trim()).filter(Boolean),
    };
    if (!payload.slug || !payload.title || !payload.meta_title || !payload.meta_description) {
      setError("Slug, title, meta title and meta description are required.");
      setBusy(false); return;
    }
    try {
      if (isNew) await axios.post(`${API}/admin/landing`, payload, authH());
      else await axios.patch(`${API}/admin/landing/${page.id}`, payload, authH());
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="landing-editor-modal">
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{isNew ? "Create" : "Edit"} SEO landing page</span>
            <h2>{isNew ? "New landing page" : form.title}</h2>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <h4 className="editor-section-title">Content</h4>
          <div className="form-grid">
            <label className="form-label">Page slug (URL)
              <input value={form.slug} onChange={e => set("slug", e.target.value)}
                placeholder="e.g. char-dham-yatra-2026" data-testid="lp-slug" />
            </label>
            <label className="form-label">Page title (H1)
              <input value={form.title} onChange={e => set("title", e.target.value)}
                placeholder="e.g. Char Dham Yatra 2026" data-testid="lp-title" />
            </label>
          </div>
          <label className="form-label">Subheadline (below title)
            <input value={form.subheadline} onChange={e => set("subheadline", e.target.value)}
              placeholder="A short hero subheading" data-testid="lp-subheadline" />
          </label>
          <label className="form-label">Hero image URL
            <input value={form.hero_image} onChange={e => set("hero_image", e.target.value)}
              placeholder="https://..." data-testid="lp-hero-image" />
          </label>
          <label className="form-label">Editorial intro paragraph
            <textarea value={form.body_intro} onChange={e => set("body_intro", e.target.value)}
              placeholder="A meaningful, 2-3 sentence intro to the journey..."
              style={{ minHeight: 100 }} data-testid="lp-intro" />
          </label>
          <label className="form-label">Highlights (one per line)
            <textarea value={highlightsStr} onChange={e => setHighlightsStr(e.target.value)}
              placeholder={`15 days · 14 nights\nReservation coach travel\nAccommodation included`}
              style={{ minHeight: 120 }} data-testid="lp-highlights" />
          </label>
          <label className="form-label">Featured yatra slugs (comma-separated)
            <input value={featuredStr} onChange={e => setFeaturedStr(e.target.value)}
              placeholder="e.g. char-dham-yatra, 7-jyotirlinga-yatra" data-testid="lp-featured" />
            <small style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', color: 'var(--ink-soft)', marginTop: 4 }}>
              Available: {packages.map(p => p.slug).join(", ")}
            </small>
          </label>

          <h4 className="editor-section-title">SEO Meta</h4>
          <label className="form-label">Meta title (60-70 chars)
            <input value={form.meta_title} onChange={e => set("meta_title", e.target.value)}
              placeholder="Char Dham Yatra 2026 — 15-Day Pilgrimage | Arth Setuu Global"
              data-testid="lp-meta-title" />
            <small style={{ color: form.meta_title?.length > 70 ? '#B33A1A' : 'var(--ink-soft)', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', marginTop: 4 }}>
              {form.meta_title?.length || 0} characters
            </small>
          </label>
          <label className="form-label">Meta description (150-160 chars)
            <textarea value={form.meta_description} onChange={e => set("meta_description", e.target.value)}
              placeholder="Join our 2026 Char Dham Yatra..." data-testid="lp-meta-desc" style={{ minHeight: 80 }} />
            <small style={{ color: form.meta_description?.length > 160 ? '#B33A1A' : 'var(--ink-soft)', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', marginTop: 4 }}>
              {form.meta_description?.length || 0} characters
            </small>
          </label>
          <label className="form-label">Keywords (comma-separated)
            <input value={form.keywords} onChange={e => set("keywords", e.target.value)}
              placeholder="char dham 2026, kedarnath badrinath yatra" data-testid="lp-keywords" />
          </label>
          <div className="form-grid">
            <label className="form-label">Canonical URL
              <input value={form.canonical_url} onChange={e => set("canonical_url", e.target.value)}
                placeholder="https://arthsetuuglobal.in/l/..." data-testid="lp-canonical" />
            </label>
            <label className="form-label">OG image URL (social share)
              <input value={form.og_image} onChange={e => set("og_image", e.target.value)}
                placeholder="Leave blank to use hero image" data-testid="lp-og-image" />
            </label>
          </div>

          <h4 className="editor-section-title">FAQs (rich results)</h4>
          <div className="departure-editor">
            {(form.faqs || []).map((f, i) => (
              <div key={i} className="dep-chip" style={{ alignItems: 'flex-start', padding: '0.7rem 0.9rem' }}>
                <div style={{ flex: 1 }}>
                  <b>{f.q}</b>
                  <small style={{ display: 'block', marginTop: 4, marginLeft: 0 }}>{f.a}</small>
                </div>
                <button className="icon-btn danger" onClick={() => removeFaq(i)}><X size={12} /></button>
              </div>
            ))}
            <div className="dep-add" style={{ gridTemplateColumns: '1fr 2fr auto' }}>
              <input placeholder="Question" value={faqQ} onChange={e => setFaqQ(e.target.value)} data-testid="lp-faq-q" />
              <input placeholder="Answer" value={faqA} onChange={e => setFaqA(e.target.value)} data-testid="lp-faq-a" />
              <button className="btn btn-outline btn-sm" onClick={addFaq} data-testid="lp-faq-add">+ Add FAQ</button>
            </div>
          </div>

          <h4 className="editor-section-title">CTA Block</h4>
          <div className="form-grid">
            <label className="form-label">CTA title
              <input value={form.cta_title} onChange={e => set("cta_title", e.target.value)} data-testid="lp-cta-title" />
            </label>
            <label className="form-label">CTA supporting text
              <input value={form.cta_text} onChange={e => set("cta_text", e.target.value)} data-testid="lp-cta-text" />
            </label>
          </div>
          <label className="checkbox-row" style={{ marginTop: '0.6rem' }}>
            <input type="checkbox" checked={form.active !== false} onChange={e => set("active", e.target.checked)} data-testid="lp-active" />
            <span>Publish (make this landing page live)</span>
          </label>

          {error && <div className="form-error" data-testid="lp-error">{error}</div>}
        </div>
        <div className="modal-foot">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="save-landing">
            {busy ? "Saving..." : isNew ? "Create landing page" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function JobEditor({ job, onClose, onSaved, authH }) {
  const isNew = !job.id;
  const [form, setForm] = useState({ ...job });
  const [reqStr, setReqStr] = useState((job.requirements || []).join("\n"));
  const [respStr, setRespStr] = useState((job.responsibilities || []).join("\n"));
  const [benStr, setBenStr] = useState((job.benefits || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const slugify = (s) => s.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

  const save = async () => {
    setError(""); setBusy(true);
    const payload = {
      ...form,
      requirements: reqStr.split("\n").map(s => s.trim()).filter(Boolean),
      responsibilities: respStr.split("\n").map(s => s.trim()).filter(Boolean),
      benefits: benStr.split("\n").map(s => s.trim()).filter(Boolean),
    };
    if (!payload.title || !payload.slug) { setError("Title and slug are required."); setBusy(false); return; }
    try {
      if (isNew) await axios.post(`${API}/admin/jobs`, payload, authH());
      else await axios.patch(`${API}/admin/jobs/${job.id}`, payload, authH());
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="job-editor-modal">
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{isNew ? "Post" : "Edit"} job</span>
            <h2>{isNew ? "New job posting" : form.title}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-job-editor"><X size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error" data-testid="job-error">{error}</div>}
          <div className="form-grid">
            <label className="form-label">Job title
              <input value={form.title} onChange={e => {
                const t = e.target.value;
                setForm(f => ({ ...f, title: t, slug: (isNew && !f.slug) ? slugify(t) : f.slug }));
              }} placeholder="e.g. Digital Marketing Executive" data-testid="job-title" />
            </label>
            <label className="form-label">Slug (URL)
              <input value={form.slug} onChange={e => set("slug", slugify(e.target.value))}
                placeholder="e.g. digital-marketing-executive" data-testid="job-slug" />
            </label>
          </div>
          <div className="form-grid">
            <label className="form-label">Category
              <select value={form.category} onChange={e => set("category", e.target.value)} data-testid="job-category">
                {["General", "Sales", "Marketing", "Operations", "HR", "Finance", "Technology", "Customer Support", "Field"].map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="form-label">Location
              <input value={form.location} onChange={e => set("location", e.target.value)}
                placeholder="e.g. Patna, Bihar" data-testid="job-location" />
            </label>
          </div>
          <div className="form-grid">
            <label className="form-label">Employment type
              <select value={form.employment_type} onChange={e => set("employment_type", e.target.value)} data-testid="job-type">
                {["Full-time", "Part-time", "Contract", "Internship"].map(t => <option key={t}>{t}</option>)}
              </select>
            </label>
            <label className="form-label">Experience
              <input value={form.experience} onChange={e => set("experience", e.target.value)}
                placeholder="e.g. 1-3 years" data-testid="job-experience" />
            </label>
          </div>
          <div className="form-grid">
            <label className="form-label">Salary range
              <input value={form.salary_range} onChange={e => set("salary_range", e.target.value)}
                placeholder="e.g. ₹2.5 – ₹4.0 LPA" data-testid="job-salary" />
            </label>
            <label className="form-label">Posted date
              <input type="date" value={form.posted_at || ""} onChange={e => set("posted_at", e.target.value)} data-testid="job-posted-at" />
            </label>
          </div>
          <label className="form-label">Short description (shown on listing)
            <textarea rows={2} value={form.short_description} onChange={e => set("short_description", e.target.value)} data-testid="job-short-desc" />
          </label>
          <label className="form-label">Full description
            <textarea rows={5} value={form.full_description} onChange={e => set("full_description", e.target.value)} data-testid="job-full-desc" />
          </label>
          <div className="form-grid">
            <label className="form-label">Requirements (one per line)
              <textarea rows={5} value={reqStr} onChange={e => setReqStr(e.target.value)} data-testid="job-requirements" />
            </label>
            <label className="form-label">Responsibilities (one per line)
              <textarea rows={5} value={respStr} onChange={e => setRespStr(e.target.value)} data-testid="job-responsibilities" />
            </label>
          </div>
          <label className="form-label">Benefits (one per line)
            <textarea rows={3} value={benStr} onChange={e => setBenStr(e.target.value)} data-testid="job-benefits" />
          </label>
          <label className="form-label" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={!!form.active} onChange={e => set("active", e.target.checked)} data-testid="job-active" />
            <span>Publish this job (visible on the public jobs page)</span>
          </label>
        </div>
        <div className="modal-foot">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="save-job">
            {busy ? "Saving..." : isNew ? "Publish job" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


function LandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/landing/${slug}`)
      .then(r => setPage(r.data))
      .catch(() => setNotFound(true));
    axios.get(`${API}/packages`).then(r => setPackages(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <>
        <Header />
        <main className="legal" data-testid="landing-not-found">
          <h1>Page not found</h1>
          <p>The landing page you requested doesn't exist. <Link to="/packages" className="link-arrow">Explore our yatras <ArrowRight size={13} /></Link></p>
        </main>
        <Footer />
        <StickyCTAs />
      </>
    );
  }
  if (!page) {
    return (
      <>
        <Header />
        <main style={{ padding: '6rem 5vw', textAlign: 'center', color: 'var(--ink-soft)' }}>
          <Sparkles size={20} /> Loading journey details...
        </main>
        <Footer />
      </>
    );
  }

  const featured = (page.featured_slugs || [])
    .map(s => packages.find(p => p.slug === s))
    .filter(Boolean);

  const faqSchema = page.faqs?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": page.faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://arthsetuuglobal.in/" },
      { "@type": "ListItem", "position": 2, "name": "Yatras", "item": "https://arthsetuuglobal.in/packages" },
      { "@type": "ListItem", "position": 3, "name": page.title, "item": page.canonical_url || `https://arthsetuuglobal.in/l/${page.slug}` }
    ]
  };

  const tripSchema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": page.title,
    "description": page.meta_description,
    "image": page.hero_image,
    "touristType": ["Pilgrim", "Spiritual Traveler"],
    "provider": {
      "@type": "TravelAgency",
      "name": "Arth Setuu Global",
      "url": "https://arthsetuuglobal.in"
    }
  };

  return (
    <>
      <SEO
        title={page.meta_title}
        description={page.meta_description}
        image={page.og_image || page.hero_image}
        canonical={page.canonical_url || `${SITE_URL}/l/${page.slug}`}
        keywords={page.keywords}
        schema={[organizationSchema(), tripSchema, breadcrumbSchema, faqSchema].filter(Boolean)}
      />
      <Header />
      <main data-testid={`landing-${page.slug}`}>
        <section className="landing-hero" style={{ backgroundImage: `url(${page.hero_image})` }}>
          <div className="landing-hero-content">
            <span className="eyebrow"><Sparkles size={13} /> Sacred Journey · 2026</span>
            <h1>{page.title}</h1>
            <p>{page.subheadline}</p>
            <div className="hero-ctas" style={{ marginTop: '2rem' }}>
              <a
                className="btn btn-whatsapp"
                href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20am%20interested%20in%20${encodeURIComponent(page.title)}`}
                target="_blank" rel="noreferrer" data-testid="landing-whatsapp-btn"
              >
                <MessageCircle size={17} /> Book on WhatsApp
              </a>
              <a className="btn btn-outline-light" href="#landing-enquiry" data-testid="landing-enquire-btn">
                Send Enquiry <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        <section className="landing-body">
          <div className="landing-lead">
            <span className="eyebrow"><Compass size={13} /> The Journey</span>
            <h2 style={{ marginTop: '0.8rem' }}>Why this <em className="italic-accent">yatra</em>?</h2>
            <p className="landing-intro">{page.body_intro}</p>
          </div>
          {page.highlights?.length > 0 && (
            <div className="landing-highlights">
              <h3 style={{ marginBottom: '1rem' }}>What's included</h3>
              <ul className="check-list">
                {page.highlights.map((h, i) => <li key={i}><Check size={17} /> {h}</li>)}
              </ul>
            </div>
          )}
        </section>

        {featured.length > 0 && (
          <section className="featured-yatras" data-testid="landing-featured">
            <div className="section-heading" style={{ textAlign: 'left', marginBottom: '3rem' }}>
              <span className="eyebrow"><Star size={13} fill="currentColor" /> Yatras You May Like</span>
              <h2 style={{ marginTop: '0.8rem' }}>Explore <em className="italic-accent">related routes</em></h2>
            </div>
            <div className="yatra-grid">
              {featured.map((p, i) => <YatraCard key={p.slug} pkg={p} large={i === 0 && featured.length >= 3} />)}
            </div>
          </section>
        )}

        {page.faqs?.length > 0 && (
          <section className="landing-faqs" data-testid="landing-faqs">
            <div className="section-heading">
              <span className="eyebrow"><Search size={13} /> Common Questions</span>
              <h2 style={{ marginTop: '0.8rem' }}>Everything you're <em className="italic-accent">wondering.</em></h2>
            </div>
            <div className="faq-list">
              {page.faqs.map((f, i) => (
                <details key={i} className="faq-item" data-testid={`faq-${i}`}>
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <section className="landing-cta" data-testid="landing-cta">
          <span className="eyebrow" style={{ color: 'var(--marigold)' }}><Sparkles size={13} /> Your Journey Awaits</span>
          <h2>{page.cta_title}</h2>
          <p>{page.cta_text}</p>
          <div className="final-cta-actions">
            <a
              className="btn btn-whatsapp"
              href={`https://wa.me/${WHATSAPP}?text=Namaste%2C%20I%20am%20interested%20in%20${encodeURIComponent(page.title)}`}
              target="_blank" rel="noreferrer" data-testid="landing-cta-whatsapp"
            >
              <MessageCircle size={17} /> Book on WhatsApp
            </a>
            <a className="btn btn-outline-light" href={`tel:${PHONE}`} data-testid="landing-cta-call">
              <Phone size={17} /> Call +91 {PHONE}
            </a>
          </div>
        </section>

        <section id="landing-enquiry">
          <EnquirySection selected={page.title} />
        </section>
      </main>
      <Footer />
      <StickyCTAs />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Umbrella />} />
          <Route path="/yatra" element={<Home />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/yatra/:slug" element={<PackagePage />} />
          <Route path="/l/:slug" element={<LandingPage />} />
          <Route path="/jobs" element={<JobsList />} />
          <Route path="/jobs/:slug" element={<JobDetail />} />
          <Route path="/loans" element={<LoansList />} />
          <Route path="/loans/:slug" element={<LoanDetail />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy-policy" element={<Legal title="Privacy & Consent" kind="privacy" />} />
          <Route path="/terms-and-conditions" element={<Legal title="Terms & Conditions" kind="terms" />} />
          <Route path="/refund-cancellation-policy" element={<Legal title="Refund & Cancellation Policy" kind="refund" />} />
          <Route path="/disclaimer" element={<Legal title="Travel Disclaimer" kind="disclaimer" />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}
